/**
 * Auth Probe — E2E Smoke Harness
 *
 * Proves FE↔BE integration by making three backend calls:
 *   1. Authenticated GET   → expects 200
 *   2. Unauthenticated GET → expects 401
 *   3. Permission-denied   → expects 403
 *
 * SECURITY: Never displays tokens. Only shows sentAuthHeader: true/false.
 */

import { useState, useCallback } from "react"
import { env } from "@/lib/env"
import { useAuthAccess } from "@/lib/auth"
import { generateRequestId } from "@/lib/api/requestId"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProbeResult {
  label: string
  url: string
  method: string
  sentAuthHeader: boolean
  requestId: string
  status: number | null
  body: string
  responseRequestId: string | null
  error: string | null
}

// ---------------------------------------------------------------------------
// Probe endpoints — adjust for your backend
// ---------------------------------------------------------------------------

/** Protected endpoint any authenticated user should reach. */
const ENDPOINT_AUTHED = "/api/me"

/** Admin-only endpoint — V2 uses companies endpoint for admin probe. */
const ENDPOINT_ADMIN = "/api/companies"

// ---------------------------------------------------------------------------
// Low-level fetch that captures everything we need for the report.
// Uses raw fetch intentionally — bypasses apiClient/adapter to isolate
// transport proof from business logic.
// ---------------------------------------------------------------------------

async function probeFetch(
  url: string,
  label: string,
  token: string | null
): Promise<ProbeResult> {
  const requestId = generateRequestId()
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Request-Id": requestId,
  }

  const sentAuthHeader = token !== null
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  try {
    const response = await fetch(url, { method: "GET", headers })

    let body: string
    const ct = response.headers.get("content-type") ?? ""
    if (ct.includes("application/json")) {
      try {
        const json = await response.json()
        body = JSON.stringify(json, null, 2)
      } catch {
        body = "(JSON parse failed)"
      }
    } else {
      body = await response.text().catch(() => "(could not read body)")
    }

    // Truncate very long bodies for display
    if (body.length > 2000) {
      body = body.slice(0, 2000) + "\n... (truncated)"
    }

    return {
      label,
      url,
      method: "GET",
      sentAuthHeader,
      requestId,
      status: response.status,
      body,
      responseRequestId: response.headers.get("x-request-id"),
      error: null,
    }
  } catch (e) {
    return {
      label,
      url,
      method: "GET",
      sentAuthHeader,
      requestId,
      status: null,
      body: "",
      responseRequestId: null,
      error: e instanceof Error ? e.message : String(e),
    }
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ProbeState = "idle" | "running" | "done"

export default function AuthProbe() {
  const { isAuthenticated, isLoading, login, getAccessToken } = useAuthAccess()
  const [state, setState] = useState<ProbeState>("idle")
  const [results, setResults] = useState<ProbeResult[]>([])

  const baseUrl = env.VITE_API_BASE_URL

  const runProbes = useCallback(async () => {
    setState("running")
    setResults([])

    const token = await getAccessToken()
    const probes: ProbeResult[] = []

    // 1) Authenticated GET — expects 200
    probes.push(
      await probeFetch(
        `${baseUrl}${ENDPOINT_AUTHED}`,
        "Authenticated (expect 200)",
        token
      )
    )

    // 2) Unauthenticated GET — expects 401
    probes.push(
      await probeFetch(
        `${baseUrl}${ENDPOINT_AUTHED}`,
        "Unauthenticated (expect 401)",
        null // deliberately omit token
      )
    )

    // 3) Permission-denied GET — expects 403
    probes.push(
      await probeFetch(
        `${baseUrl}${ENDPOINT_ADMIN}`,
        "Permission-denied (expect 403)",
        token
      )
    )

    setResults(probes)
    setState("done")
  }, [baseUrl, getAccessToken])

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div style={{ padding: 24, fontFamily: "monospace", fontSize: 13, maxWidth: 900 }}>
      <h1 style={{ fontSize: 18, marginBottom: 4 }}>Auth Probe — E2E Smoke Harness</h1>
      <p style={{ color: "#888", marginBottom: 16 }}>
        Target: <strong>{baseUrl}</strong> | Shell mode: <strong>{env.VITE_UI_SHELL_MODE}</strong> |
        Environment: <strong>{env.VITE_ENVIRONMENT}</strong>
      </p>

      {/* Auth state */}
      {isLoading && <p>Loading auth state...</p>}
      {!isLoading && !isAuthenticated && (
        <div style={{ marginBottom: 16 }}>
          <p>Not authenticated.</p>
          <button
            type="button"
            onClick={() => login("/__probe")}
            style={buttonStyle}
          >
            Sign in with Auth0
          </button>
        </div>
      )}
      {!isLoading && isAuthenticated && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: "#4caf50" }}>Authenticated (token available)</p>
          <button
            type="button"
            onClick={runProbes}
            disabled={state === "running"}
            style={buttonStyle}
          >
            {state === "running" ? "Running probes..." : "Run All Probes"}
          </button>
        </div>
      )}

      {/* Results */}
      {results.map((r, i) => (
        <div key={i} style={cardStyle}>
          <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>
            {statusBadge(r.status)} {r.label}
          </h3>
          <Row label="URL" value={r.url} />
          <Row label="Method" value={r.method} />
          <Row label="sentAuthHeader" value={String(r.sentAuthHeader)} />
          <Row label="X-Request-Id (sent)" value={r.requestId} />
          <Row label="X-Request-Id (response)" value={r.responseRequestId ?? "(not returned)"} />
          <Row label="Status" value={r.status !== null ? String(r.status) : "N/A"} />
          {r.error && <Row label="Error" value={r.error} />}
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: "pointer", color: "#888" }}>Response body</summary>
            <pre style={{ margin: "4px 0 0", whiteSpace: "pre-wrap", fontSize: 12, background: "#111", color: "#ddd", padding: 8, borderRadius: 4 }}>
              {r.body || "(empty)"}
            </pre>
          </details>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 8, lineHeight: 1.8 }}>
      <span style={{ color: "#888", minWidth: 180 }}>{label}:</span>
      <span style={{ wordBreak: "break-all" }}>{value}</span>
    </div>
  )
}

function statusBadge(status: number | null): string {
  if (status === null) return "[ERR]"
  if (status >= 200 && status < 300) return "[200 OK]"
  if (status === 401) return "[401]"
  if (status === 403) return "[403]"
  return `[${status}]`
}

const buttonStyle: React.CSSProperties = {
  padding: "8px 20px",
  fontSize: 13,
  fontFamily: "monospace",
  cursor: "pointer",
  border: "1px solid #555",
  borderRadius: 4,
  background: "#222",
  color: "#eee",
}

const cardStyle: React.CSSProperties = {
  border: "1px solid #333",
  borderRadius: 6,
  padding: 16,
  marginBottom: 12,
  background: "#1a1a1a",
  color: "#eee",
}
